import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wrench, Zap, Hammer, PaintBucket, LayoutGrid, Snowflake, Layers,
  Plus, Camera, Trash2, X, ArrowLeft, Pencil, Loader2, ImageOff,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Les 7 caisses par metier (fixes). Les classes Tailwind sont ecrites en toutes
// lettres pour que le build les conserve (pas de concatenation dynamique).
const CAISSES = [
  { key: 'plomberie', icon: Wrench, tile: 'from-blue-50 to-blue-100 border-blue-200', iconBg: 'bg-blue-600', text: 'text-blue-700',
    label: ['Caisse à plomberie', 'Ящик сантехника', 'Cutie instalații', 'Скринька сантехніка'] },
  { key: 'electricite', icon: Zap, tile: 'from-amber-50 to-amber-100 border-amber-200', iconBg: 'bg-amber-500', text: 'text-amber-700',
    label: ['Caisse électricité', 'Ящик электрика', 'Cutie electricitate', 'Скринька електрика'] },
  { key: 'outils', icon: Hammer, tile: 'from-slate-50 to-slate-100 border-slate-200', iconBg: 'bg-slate-600', text: 'text-slate-700',
    label: ['Caisse à outils', 'Ящик инструментов', 'Cutie scule', 'Скринька інструментів'] },
  { key: 'enduit_placo', icon: PaintBucket, tile: 'from-orange-50 to-orange-100 border-orange-200', iconBg: 'bg-orange-500', text: 'text-orange-700',
    label: ['Caisse à enduit et placo', 'Ящик штукатурка и гипсокартон', 'Cutie glet și rigips', 'Скринька шпаклівка і гіпсокартон'] },
  { key: 'carrelage', icon: LayoutGrid, tile: 'from-teal-50 to-teal-100 border-teal-200', iconBg: 'bg-teal-600', text: 'text-teal-700',
    label: ['Caisse à carrelage', 'Ящик плитка', 'Cutie faianță', 'Скринька плитка'] },
  { key: 'climatisation', icon: Snowflake, tile: 'from-cyan-50 to-cyan-100 border-cyan-200', iconBg: 'bg-cyan-500', text: 'text-cyan-700',
    label: ['Climatisation', 'Кондиционер', 'Climatizare', 'Кондиціонер'] },
  { key: 'multicouche', icon: Layers, tile: 'from-purple-50 to-purple-100 border-purple-200', iconBg: 'bg-purple-600', text: 'text-purple-700',
    label: ['Caisse multicouche', 'Ящик многослойных труб', 'Cutie multistrat', 'Скринька багатошарова'] },
];

const LANG_INDEX = { fr: 0, ru: 1, md: 2, uk: 3 };

// Compresse une image cote client (comme le module equipements) avant l'envoi.
function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.82));
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ToolsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isManager = user?.role === 'manager';
  const li = LANG_INDEX[language] ?? 0;
  const txt = (fr, ru, md, uk) => [fr, ru, md, uk][li];

  const [counts, setCounts] = useState({});
  const [activeCat, setActiveCat] = useState(null);   // null => grille des caisses
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pending, setPending] = useState(null);       // {dataUrl, name} avant enregistrement
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);  // envoi groupe (plusieurs photos)
  const [selected, setSelected] = useState(null);     // photo ouverte en grand
  const fileRef = useRef(null);

  const caisse = CAISSES.find(c => c.key === activeCat) || null;

  const loadCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tools/counts`);
      setCounts(r.data || {});
    } catch (e) { /* silencieux */ }
  }, []);

  const loadPhotos = useCallback(async (cat) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tools`, { params: { category: cat, thumbs_only: true } });
      setPhotos(r.data || []);
    } catch (e) {
      toast.error(txt('Erreur de chargement', 'Ошибка загрузки', 'Eroare la încărcare', 'Помилка завантаження'));
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const openCaisse = (cat) => { setActiveCat(cat); loadPhotos(cat); };
  const backToCaisses = () => { setActiveCat(null); setPhotos([]); loadCounts(); };

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    // Une seule photo : apercu + legende avant enregistrement.
    if (files.length === 1) {
      try {
        const dataUrl = await fileToCompressedDataUrl(files[0]);
        setPending({ dataUrl, name: '' });
      } catch (err) {
        toast.error(txt("Impossible de lire l'image", 'Не удалось прочитать изображение', "Nu s-a putut citi imaginea", 'Не вдалося прочитати зображення'));
      }
      return;
    }

    // Plusieurs photos (bibliotheque) : envoi direct, legende editable ensuite.
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const dataUrl = await fileToCompressedDataUrl(f);
        await axios.post(`${API}/tools`, { category: activeCat, name: '', photo_data: dataUrl });
        ok += 1;
      } catch (err) { /* on continue les suivantes */ }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} ${txt('photo(s) ajoutée(s)', 'фото добавлено', 'fotografii adăugate', 'фото додано')}`);
      loadPhotos(activeCat);
    } else {
      toast.error(txt("Échec de l'ajout", 'Ошибка добавления', "Eroare la adăugare", 'Помилка додавання'));
    }
  };

  const savePending = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await axios.post(`${API}/tools`, {
        category: activeCat, name: pending.name || '', photo_data: pending.dataUrl,
      });
      setPending(null);
      toast.success(txt('Photo ajoutée', 'Фото добавлено', 'Fotografie adăugată', 'Фото додано'));
      loadPhotos(activeCat);
    } catch (err) {
      toast.error(err?.response?.data?.detail || txt("Échec de l'ajout", 'Ошибка добавления', "Eroare la adăugare", 'Помилка додавання'));
    } finally { setSaving(false); }
  };

  const openPhoto = async (id) => {
    try {
      const r = await axios.get(`${API}/tools/${id}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(txt('Photo introuvable', 'Фото не найдено', 'Fotografie negăsită', 'Фото не знайдено'));
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm(txt('Supprimer cette photo ?', 'Удалить это фото?', 'Ștergeți această fotografie?', 'Видалити це фото?'))) return;
    try {
      await axios.delete(`${API}/tools/${id}`);
      setSelected(null);
      toast.success(txt('Photo supprimée', 'Фото удалено', 'Fotografie ștearsă', 'Фото видалено'));
      loadPhotos(activeCat);
    } catch (e) {
      toast.error(txt('Échec de la suppression', 'Ошибка удаления', 'Eroare la ștergere', 'Помилка видалення'));
    }
  };

  const saveLegend = async () => {
    if (!selected) return;
    try {
      await axios.put(`${API}/tools/${selected.id}`, { name: selected.name || '' });
      toast.success(txt('Légende enregistrée', 'Подпись сохранена', 'Legendă salvată', 'Підпис збережено'));
      setSelected(null);
      loadPhotos(activeCat);
    } catch (e) {
      toast.error(txt('Échec', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  // Glisser-deposer : tap pour ouvrir, appui long (mobile) ou 8px (souris) pour deplacer.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photos.findIndex(p => p.id === active.id);
    const newIndex = photos.findIndex(p => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(photos, oldIndex, newIndex);
    setPhotos(next);   // optimiste
    try {
      await axios.put(`${API}/tools/reorder`, { category: activeCat, ids: next.map(p => p.id) });
    } catch (e) {
      toast.error(txt("Ordre non enregistré", 'Порядок не сохранён', "Ordinea nu a fost salvată", 'Порядок не збережено'));
      loadPhotos(activeCat);   // on resynchronise
    }
  };

  // ------------------------- Vue : grille des caisses -------------------------
  if (!caisse) {
    return (
      <div className="page-container" data-testid="tools-page">
        <div className="flex items-center gap-2 mb-6">
          <h1 className="page-title flex items-center gap-2">
            <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-lg sm:text-xl">{txt('Outils', 'Инструменты', 'Unelte', 'Інструменти')}</span>
          </h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {txt('Choisissez une caisse pour voir les photos des outils.',
               'Выберите ящик, чтобы посмотреть фото инструментов.',
               'Alegeți o cutie pentru a vedea fotografiile uneltelor.',
               'Оберіть скриньку, щоб переглянути фото інструментів.')}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {CAISSES.map((c) => {
            const Icon = c.icon;
            const n = counts[c.key] || 0;
            return (
              <button
                key={c.key}
                onClick={() => openCaisse(c.key)}
                data-testid={`caisse-${c.key}`}
                className={`card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br ${c.tile}`}
              >
                <div className={`w-12 h-12 ${c.iconBg} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className={`font-semibold ${c.text} text-sm text-center leading-tight`}>{c.label[li]}</span>
                <span className="text-xs text-gray-500">
                  {n} {txt('photo(s)', 'фото', 'foto', 'фото')}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ------------------------- Vue : galerie d'une caisse -------------------------
  const Icon = caisse.icon;
  return (
    <div className="page-container" data-testid="tools-gallery">
      <div className="flex items-center justify-between mb-6 gap-2">
        <h1 className="page-title flex items-center gap-2 min-w-0 flex-1">
          <button onClick={backToCaisses} className="p-2 -ml-2 rounded-lg hover:bg-gray-100" data-testid="back-caisses">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
          <span className="truncate text-lg sm:text-xl">{caisse.label[li]}</span>
        </h1>
        {isManager && (
          <>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFile} data-testid="tool-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-primary flex items-center justify-center min-w-10 h-10 px-3 sm:px-4 rounded-full sm:rounded-lg flex-shrink-0 shadow-lg disabled:opacity-60"
              data-testid="add-tool-btn"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              <span className="hidden sm:inline ml-2">{txt('Ajouter des photos', 'Добавить фото', 'Adaugă fotografii', 'Додати фото')}</span>
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
      ) : photos.length === 0 ? (
        <div className="card-base p-8 text-center text-gray-500">
          <ImageOff className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{txt('Aucune photo dans cette caisse', 'В этом ящике нет фото', 'Nicio fotografie în această cutie', 'У цій скриньці немає фото')}</p>
          {isManager && (
            <button onClick={() => fileRef.current?.click()} className="btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg">
              <Camera className="w-4 h-4" /> {txt('Ajouter une photo', 'Добавить фото', 'Adaugă o fotografie', 'Додати фото')}
            </button>
          )}
        </div>
      ) : isManager ? (
        <>
          <p className="text-xs text-gray-400 mb-2">
            {txt('Astuce : maintenez une photo pour la déplacer.',
                 'Совет: удерживайте фото, чтобы переместить.',
                 'Sfat: țineți apăsat pe o fotografie pentru a o muta.',
                 'Порада: утримуйте фото, щоб перемістити.')}
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={photos.map(p => p.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((p) => (
                  <SortableThumb key={p.id} photo={p} onOpen={() => openPhoto(p.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <button key={p.id} onClick={() => openPhoto(p.id)} className="group relative rounded-xl overflow-hidden bg-gray-100 aspect-square shadow-sm hover:shadow-md transition-shadow" data-testid={`tool-thumb-${p.id}`}>
              <img src={p.thumbnail} alt={p.name || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
              {p.name ? (
                <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-xs px-2 py-1 text-left truncate">{p.name}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {/* Dialog : confirmer l'ajout (apercu + legende) */}
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{txt('Nouvelle photo', 'Новое фото', 'Fotografie nouă', 'Нове фото')}</DialogTitle></DialogHeader>
          {pending && (
            <div className="space-y-3">
              <img src={pending.dataUrl} alt="" className="w-full max-h-72 object-contain rounded-lg bg-gray-50" />
              <div>
                <Label className="text-sm">{txt("Nom de l'outil (facultatif)", 'Название инструмента (необяз.)', "Numele uneltei (opțional)", 'Назва інструмента (необов.)')}</Label>
                <Input value={pending.name} onChange={(e) => setPending(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={txt('Ex : perceuse', 'Напр.: дрель', 'Ex: bormașină', 'Напр.: дриль')} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setPending(null)} className="px-4 py-2 rounded-lg border text-sm">{txt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}</button>
                <button onClick={savePending} disabled={saving} className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog : photo en grand */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="pr-6">{selected?.name || txt('Photo', 'Фото', 'Fotografie', 'Фото')}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <img src={selected.photo_data || selected.thumbnail} alt={selected.name || ''} className="w-full max-h-[60vh] object-contain rounded-lg bg-gray-50" />
              {isManager ? (
                <>
                  <div>
                    <Label className="text-sm flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> {txt('Légende', 'Подпись', 'Legendă', 'Підпис')}</Label>
                    <Input value={selected.name || ''} onChange={(e) => setSelected(prev => ({ ...prev, name: e.target.value }))} className="mt-1" />
                  </div>
                  <div className="flex justify-between gap-2 pt-1">
                    <button onClick={() => deletePhoto(selected.id)} className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm inline-flex items-center gap-2">
                      <Trash2 className="w-4 h-4" /> {txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                    </button>
                    <button onClick={saveLegend} className="btn-primary px-4 py-2 rounded-lg text-sm">{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}</button>
                  </div>
                </>
              ) : (
                selected.created_by_name ? (
                  <p className="text-xs text-gray-400">{txt('Ajouté par', 'Добавил', 'Adăugat de', 'Додав')} {selected.created_by_name}</p>
                ) : null
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Vignette deplaçable (grille). Tap = ouvrir ; appui long / glisser = reordonner.
function SortableThumb({ photo, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 1,
    touchAction: 'none',
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      className="group relative rounded-xl overflow-hidden bg-gray-100 aspect-square shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
      data-testid={`tool-thumb-${photo.id}`}
    >
      <img src={photo.thumbnail} alt={photo.name || ''} className="w-full h-full object-cover pointer-events-none" loading="lazy" />
      {photo.name ? (
        <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-xs px-2 py-1 text-left truncate">{photo.name}</span>
      ) : null}
    </button>
  );
}
